import Vue from "vue";
import _ from 'lodash';
import {BASE_API_URL} from '@/store';
import {extendCustomUserFields} from '@/utils/auth/extendCustomUserFields'
import {createAuthAttributes} from '@/utils/auth/createAuthAttributes';
import {fetchOnce} from '@/store/_utils';

const app = {
  state: {
    appSettings: null,
    customUserFields: null,
    extensionSettings: null,
    loadingExtensionSettings: true,
    registrationAttributes: null,
    buildTimestamp: process.env.BUILD_TIMESTAMP,
    pushNotification: {},
    installPWAEvent: null,
    showPushNotification: false,
    pushNotificationRegistrationState: {},
    loyaltyScan: {},
    serviceWorkerUpdated: null,
    bannerSlides: {},
    appScreens: {},
    fetched: {
      bannerSlides: false,
      extended_bounty: false
    },
    hideAppUI: false
  },
  mutations: {
    SET_APP_SETTINGS(state, payload) {
      state.appSettings = payload;
    },
    SET_CUSTOM_USERFIELDS(state, payload) {
      state.customUserFields = payload;
    },
    SET_EXTENSION_SETTINGS(state, payload) {
      state.extensionSettings = payload;
    },
    SET_LOADING_EXTENSION_SETTINGS(state, status) {
      state.loadingExtensionSettings = status
    },
    SET_REGISTRATION_ATTRIBUTES(state, payload) {
      state.registrationAttributes = payload;
    },
    SET_INSTALL_PWA_EVENT(state, payload) {
      state.installPWAEvent = payload;
    },
    SET_PUSHNOTIFICATION(state, payload) {
      state.pushNotification = payload
    },
    SET_PUSHNOTIFICATION_REQUEST(state, payload) {
      state.showPushNotification = payload;
    },
    SET_PUSHNOTIFICATION_REGISTRATION_STATE(state, payload) {
      state.pushNotificationRegistrationState = payload;
    },
    SET_LOYALTY_SCAN(state, payload) {
      state.loyaltyScan = payload
    },
    SET_SERVICEWORKER_UPDATED(state, payload) {
      state.serviceWorkerUpdated = payload;
    },
    SET_BANNER_SLIDES(state, payload) {
      state.bannerSlides = payload;
    },
    SET_APP_SCREENS(state, payload) {
      state.appScreens = payload;
    },
    SET_DEFAULT_APP_SCREENS(state) {
      state.appScreens = {
        loyalty: {
          type: 'generic',
          components: [
            {
              name: 'Profile'
            },
            {
              name: 'BannerSlider'
            },
            {
              name: 'InstallWebappPopup',
              data: {
                persistent: false
              }
            },
            {
              name: 'BountyCarousel'
            },
            {
              name: 'SweepstakesOverview'
            },
            {
              name: 'HelpSection'
            },
            {
              name: 'ActivityFeed',
              data: {
                preview: true
              }
            }
          ]
        }
      }
    },
    setHideAppUI(state, hide) {
      state.hideAppUI = hide
    }
  },
  actions: {
    async fetchAppSettings({rootState, commit}) {
      let response = await Vue.axios.get(`${BASE_API_URL}/api/v1/clients/${rootState.client_id}/app-settings/`);
      commit('SET_APP_SETTINGS', response.data);
    },
    async fetchExtensionSettings({rootState, commit}) {
      try {
        const response = await Vue.axios.get(
          `${BASE_API_URL}/api/v1/clients/${rootState.client_id}/extensions/`
        );
        const appScreens = _.get(response.data, 'webview.screens', []);

        commit('SET_EXTENSION_SETTINGS', response.data);

        if (_.isEmpty(appScreens)) {
          commit('SET_DEFAULT_APP_SCREENS');
        } else {
          commit('SET_APP_SCREENS', appScreens);
        }

        commit('SET_LOADING_EXTENSION_SETTINGS', false);
      } catch (error) {
        commit('SET_DEFAULT_APP_SCREENS');
        commit('SET_LOADING_EXTENSION_SETTINGS', false);
        console.log('Error fetching extension settings', error);
      }
    },
    async fetchCustomUserFields({rootState, commit}) {
      let response = await Vue.axios.get(`${BASE_API_URL}/api/v1/clients/${rootState.client_id}/custom-fields/`);
      const extendedCustomUserFields = extendCustomUserFields(response.data);

      // TODO: once
      commit('SET_CUSTOM_USERFIELDS', extendedCustomUserFields);
      commit('SET_REGISTRATION_ATTRIBUTES', createAuthAttributes());
    },
    installPWAEvent({commit}, evt) {
      commit('SET_INSTALL_PWA_EVENT', evt);
    },
    setPushNotification({commit}, obj) {
      commit('SET_PUSHNOTIFICATION', obj)
    },
    showPushNotificationRequest({commit}, show) {
      commit('SET_PUSHNOTIFICATION_REQUEST', show);
    },
    pushNotificationRegistration({commit}, obj) {
      commit('SET_PUSHNOTIFICATION_REGISTRATION_STATE', obj);
    },
    setLoyaltyScan({commit}, obj) {
      commit('SET_LOYALTY_SCAN', obj);
    },
    setServiceWorkerUpdated({commit}, reg) {
      commit('SET_SERVICEWORKER_UPDATED', reg)
    },
    async fetchBannerSlides(context, {next, once, key}) {
      key = key || 'banner1';
      return fetchOnce(context, once, next, 'bannerSlides', () => {
        return Vue.axios.get(`${BASE_API_URL}/api/v1/clients/${context.rootState.client_id}/app-banner/${key}/`).then(function(response) {
          context.commit('SET_BANNER_SLIDES', response.data);
        }).then(() => {
          if (next) {
            next()
          }
        }).catch((error) => {
          console.warn("Error fetching subclients: " + error)
        })
      })
    },
    async fetchCmsModel(_, key) {
      try {
        const response = await Vue.axios.get(
          `${BASE_API_URL}/api/v1/cms/model/${key}/`
        )

        return response.data
      } catch (error) {
        return {
          error: 'FETCH_CMS_MODEL_FAILED'
        }
      }
    },
    async fetchAppPage({rootState}, pageId) {
      try {
        const response = await Vue.axios.get(
          `${BASE_API_URL}/api/v1/clients/${rootState.client_id}/app-page/${pageId}`
        )

        return response.data
      } catch (error) {
        return {
          error: 'FETCH_APP_PAGE_FAILED'
        }
      }
    },
    async fetchPointPoolRelationships({rootState}) {
      try {
        const response = await Vue.axios.get(
          `${BASE_API_URL}/api/v1/clients/${rootState.client_id}/point-pool-relationships/`
        )

        return response.data
      } catch (error) {
        return {
          error: 'FETCH_POINT_POOL_RELATIONSHIPS_FAILED'
        }
      }
    },
    async fetchInitialData({rootState, dispatch}) {
      Vue.axios.defaults.headers['Helloagain-App-Id'] = rootState.client_id
      await dispatch('fetchAppSettings')
      await dispatch('fetchExtensionSettings')
      await dispatch('fetchCustomUserFields')
      await dispatch('fetchClient')
    }
  },
  getters: {
    appSettings(state) {
      return state.appSettings;
    },
    customUserFields(state) {
      return state.customUserFields;
    },
    extensionSettings(state) {
      return state.extensionSettings;
    },
    authSettings(state) {
      return state.extensionSettings.auth;
    },
    webviewSettings(state) {
      return state.extensionSettings.webview;
    },
    navigationSettings(state) {
      return state.extensionSettings.navigation;
    },
    registrationAttributes(state) {
      return state.registrationAttributes;
    },
    installPWAEvent(state) {
      return state.installPWAEvent;
    },
    pushNotification(state) {
      return state.pushNotification;
    },
    loyaltyScan(state) {
      return state.loyaltyScan;
    },
    serviceWorkerUpdated(state) {
      return state.serviceWorkerUpdated;
    },
    bannerSlides(state) {
      return state.bannerSlides
    },
    appScreens(state) {
      return state.appScreens
    },
    loadingExtensionSettings(state) {
      return state.loadingExtensionSettings
    },
    hideAppUI(state) {
      return state.hideAppUI
    }
  }
}

export default app;
